import { useEffect, useState, type FormEvent } from "react";
import { Icon } from "../components/Icon.js";
import { Avatar } from "../components/Avatar.js";
import {
  ApiError,
  createInvitation,
  deleteUser,
  listInvitations,
  listUsers,
  type AdminUser,
  type Invitation,
} from "../api/client.js";

export function UsersPage() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  async function refresh() {
    setError(null);
    try {
      const [u, i] = await Promise.all([listUsers(), listInvitations()]);
      setUsers(u.users);
      setInvitations(i.invitations);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load users.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function onDelete(id: string, email: string) {
    if (!confirm(`Permanently delete ${email}? This cascades to their connectors and events.`)) return;
    try {
      await deleteUser(id);
      await refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Delete failed.");
    }
  }

  return (
    <div className="flex flex-col gap-lg">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-headline-md text-on-background">Organisation Users</h1>
          <p className="text-body-sm text-on-surface-variant mt-xs">
            Active accounts and pending invitations across the AppsDo tenant.
          </p>
        </div>
        <button
          onClick={() => setInviteOpen(true)}
          className="bg-primary-container text-on-primary-container px-md py-sm rounded-lg text-label-xs font-mono uppercase tracking-widest flex items-center gap-sm active:scale-[0.98] transition-transform"
        >
          <Icon name="person_add" />
          Invite user
        </button>
      </div>

      {error && (
        <div className="bg-error-container/20 border border-error/30 text-error rounded-lg p-3 text-body-sm flex items-center gap-2">
          <Icon name="error" /> {error}
        </div>
      )}

      {/* Active users */}
      <Section title="Active accounts" count={users.length}>
        {loading ? (
          <Skeleton rows={3} />
        ) : users.length === 0 ? (
          <Empty icon="group" message="No accounts yet." />
        ) : (
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-outline-variant bg-surface-container-high/50">
                <Th>User</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th className="text-right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/30">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-surface-container-highest/30 transition-colors">
                  <Td>
                    <div className="flex items-center gap-md">
                      <Avatar name={u.name ?? u.email} tone="primary" />
                      <div>
                        <div className="text-body-base text-on-surface font-semibold">
                          {u.name ?? <span className="text-on-surface-variant italic">no name</span>}
                        </div>
                        <div className="text-label-xs font-mono text-outline">{u.email}</div>
                      </div>
                    </div>
                  </Td>
                  <Td>
                    <span
                      className={`px-2 py-0.5 text-[10px] font-mono uppercase rounded-full border ${
                        u.isAdmin
                          ? "bg-primary-container/20 text-primary-fixed-dim border-primary-fixed-dim/30"
                          : "bg-surface-variant text-on-surface-variant border-outline-variant"
                      }`}
                    >
                      {u.isAdmin ? "Admin" : "Member"}
                    </span>
                  </Td>
                  <Td>
                    <span className="text-body-sm text-on-surface-variant capitalize">{u.status}</span>
                  </Td>
                  <Td>
                    <span className="text-body-sm font-mono text-on-surface-variant">
                      {new Date(u.createdAt).toLocaleDateString()}
                    </span>
                  </Td>
                  <Td className="text-right">
                    <button
                      onClick={() => onDelete(u.id, u.email)}
                      title="Delete user"
                      className="text-outline hover:text-error transition-colors p-1 rounded-md hover:bg-error/10"
                    >
                      <Icon name="delete" />
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {/* Pending invitations */}
      <Section
        title="Invitations"
        count={invitations.filter((i) => i.status === "pending").length}
        subtitle="Copy the accept-URL and email it to the colleague."
      >
        {invitations.length === 0 ? (
          <Empty icon="mark_email_read" message="No invitations issued yet." />
        ) : (
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-outline-variant bg-surface-container-high/50">
                <Th>Email</Th>
                <Th>Status</Th>
                <Th>Expires</Th>
                <Th>Accept URL</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/30">
              {invitations.map((inv) => (
                <InvitationRow key={inv.id} inv={inv} />
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {inviteOpen && (
        <InviteModal
          onClose={() => setInviteOpen(false)}
          onSent={() => {
            setInviteOpen(false);
            void refresh();
          }}
        />
      )}
    </div>
  );
}

function InvitationRow({ inv }: { inv: Invitation }) {
  const [copied, setCopied] = useState(false);
  const tone =
    inv.status === "pending"
      ? "bg-tertiary-container/20 text-tertiary-container border-tertiary-container/30"
      : inv.status === "accepted"
        ? "bg-primary-container/20 text-primary-fixed-dim border-primary-fixed-dim/30"
        : "bg-error-container/20 text-error border-error/30";

  async function copy() {
    try {
      await navigator.clipboard.writeText(inv.acceptUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore — user can still select the URL manually
    }
  }

  return (
    <tr className="hover:bg-surface-container-highest/30 transition-colors">
      <Td>
        <span className="text-body-sm text-on-surface font-mono">{inv.email}</span>
      </Td>
      <Td>
        <span className={`px-2 py-0.5 text-[10px] font-mono uppercase rounded-full border ${tone}`}>
          {inv.status}
        </span>
      </Td>
      <Td>
        <span className="text-body-sm font-mono text-on-surface-variant">
          {new Date(inv.expiresAt).toLocaleString()}
        </span>
      </Td>
      <Td>
        {inv.status === "pending" ? (
          <button
            onClick={copy}
            className="flex items-center gap-1.5 text-label-xs font-mono uppercase text-primary-fixed-dim hover:underline"
            title={inv.acceptUrl}
          >
            <Icon name={copied ? "check" : "content_copy"} className="text-[14px]" />
            {copied ? "Copied" : "Copy URL"}
          </button>
        ) : (
          <span className="text-on-surface-variant text-label-xs font-mono">—</span>
        )}
      </Td>
    </tr>
  );
}

function InviteModal({ onClose, onSent }: { onClose: () => void; onSent: () => void }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await createInvitation({ email, name: name.trim() || undefined, isAdmin });
      onSent();
    } catch (err) {
      if (err instanceof ApiError && err.code === "user_exists") {
        setError("A user with that email already exists.");
      } else {
        setError(err instanceof Error ? err.message : "Couldn't issue invitation.");
      }
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="glass-card-elevated w-full max-w-md rounded-xl p-6 space-y-4"
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-headline-md text-on-surface">Invite a colleague</h2>
            <p className="text-body-sm text-on-surface-variant mt-1">
              We'll generate a one-time URL valid for 7 days.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-on-surface-variant hover:text-on-surface">
            <Icon name="close" />
          </button>
        </div>
        <ModalField id="invite-email" label="Email" type="email" placeholder="colleague@appsdo.in" value={email} onChange={setEmail} required />
        <ModalField id="invite-name" label="Name (optional)" placeholder="Full name" value={name} onChange={setName} />
        <label className="flex items-center gap-3 px-3 py-2 bg-surface-container-low border border-outline-variant rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={isAdmin}
            onChange={(e) => setIsAdmin(e.target.checked)}
            className="rounded border-outline-variant bg-transparent text-primary-fixed-dim focus:ring-0"
          />
          <span className="text-body-sm text-on-surface">Grant admin role</span>
        </label>
        {error && (
          <div className="bg-error-container/20 border border-error/30 text-error rounded-lg p-3 text-body-sm flex items-center gap-2">
            <Icon name="error" /> {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-label-xs font-mono uppercase text-on-surface-variant hover:text-on-surface"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2 bg-primary-container text-on-primary-container rounded-lg text-label-xs font-mono uppercase tracking-widest disabled:opacity-60"
          >
            {submitting ? "Issuing…" : "Send invite"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ModalField({
  id,
  label,
  placeholder,
  value,
  onChange,
  type = "text",
  required,
}: {
  id: string;
  label: string;
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-label-xs font-mono text-outline uppercase tracking-widest">
        {label}
      </label>
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full bg-surface-container-lowest border border-outline-variant rounded-lg px-3 py-2 text-on-surface text-body-sm placeholder:text-outline/50 focus:outline-none focus:border-primary-container"
      />
    </div>
  );
}

function Section({
  title,
  count,
  subtitle,
  children,
}: {
  title: string;
  count?: number;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-surface-container border border-outline-variant rounded-xl overflow-hidden shadow-xl shadow-black/20">
      <div className="px-md py-md border-b border-outline-variant flex items-center justify-between">
        <div>
          <h2 className="text-headline-md text-on-surface">
            {title}
            {typeof count === "number" && (
              <span className="ml-3 text-label-xs font-mono text-on-surface-variant uppercase">
                {count}
              </span>
            )}
          </h2>
          {subtitle && <p className="text-body-sm text-on-surface-variant mt-1">{subtitle}</p>}
        </div>
      </div>
      <div className="overflow-x-auto scrollbar-custom">{children}</div>
    </div>
  );
}

function Th({ children, className = "" }: { children?: React.ReactNode; className?: string }) {
  return (
    <th
      className={`px-md py-md text-label-xs font-mono text-on-surface-variant uppercase tracking-widest ${className}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-md py-md ${className}`}>{children}</td>;
}

function Empty({ icon, message }: { icon: string; message: string }) {
  return (
    <div className="text-center py-12 text-on-surface-variant">
      <Icon name={icon} className="text-primary-fixed-dim/40" style={{ fontSize: "48px" }} />
      <p className="mt-3 text-body-sm">{message}</p>
    </div>
  );
}

function Skeleton({ rows }: { rows: number }) {
  return (
    <div className="p-md space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-12 bg-surface-container-low rounded animate-pulse" />
      ))}
    </div>
  );
}
